import React, { useEffect, useRef } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';

const ScrollReveal = ({ 
  children, 
  direction = "up", 
  delay = 0, 
  duration = 0.5,
  threshold = 0.1,
  className = "",
  staggerChildren = false,
  staggerDelay = 0.1
}) => {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: threshold });

  const directionOffset = {
    up: { y: 40 },
    down: { y: -40 },
    left: { x: 40 },
    right: { x: -40 },
    scale: { scale: 0.9 },
    none: { x: 0, y: 0 }
  };

  const initial = {
    opacity: 0,
    ...directionOffset[direction]
  };

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  const childVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: i => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * staggerDelay,
        duration: duration,
        ease: [0.25, 0.1, 0.25, 1]
      }
    })
  };

  const parentVariants = {
    hidden: initial,
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      transition: {
        duration: duration,
        delay: delay,
        ease: [0.25, 0.1, 0.25, 1],
        when: "beforeChildren",
        staggerChildren: staggerChildren ? staggerDelay : 0
      }
    }
  };

  if (staggerChildren && React.Children.count(children) > 0) {
    return (
      <motion.div
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={parentVariants}
        className={className}
      >
        {React.Children.map(children, (child, i) => (
          <motion.div 
            custom={i}
            variants={childVariants}
          >
            {child}
          </motion.div>
        ))}
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={parentVariants}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default ScrollReveal;